// @file Manages whiteboard attachments

import { useReactiveSet } from '@@/bits/reactivity'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import type { Cid, Id } from '@@/types'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import type { Link } from '@padlet/arvo'
import type { PadletAttachmentShape, PadletAttachmentShapeProps } from '@padlet/whiteboard-schema'
import { defineStore } from 'pinia'
import { type TLShapeId } from 'tldraw'
import { computed } from 'vue'

export const WHITEBOARD_ATTACHMENTS_LOADED_ID = 'whiteboard-attachments-loaded'

export const useWhiteboardAttachmentsStore = defineStore('whiteboardAttachments', () => {
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const whiteboardStore = useWhiteboardStore()

  const postCidsForLoadedAttachments = useReactiveSet<Cid>()
  const postCidsForErroredAttachments = useReactiveSet<Cid>()

  const addPostCidToLoadedAttachments = (attachmentLoadedPostCid: Cid | Id): void => {
    const cid = typeof attachmentLoadedPostCid === 'number' ? `c${attachmentLoadedPostCid}` : attachmentLoadedPostCid
    postCidsForLoadedAttachments.value.add(cid)
  }

  const addPostCidToErroredAttachments = (attachmentErroredPostCid: Cid | Id): void => {
    const cid = typeof attachmentErroredPostCid === 'number' ? `c${attachmentErroredPostCid}` : attachmentErroredPostCid
    postCidsForErroredAttachments.value.add(cid)
  }

  const currentFramePostCids = computed<Cid[]>(() => {
    const { editor } = useTldrawEditor()
    if (editor == null) return []
    const currentPageShapeIds =
      whiteboardStore.currentFrameId != null ? Array.from(editor.getPageShapeIds(whiteboardStore.currentFrameId)) : []
    const attachmentShapes = currentPageShapeIds
      .map((shapeId) => {
        const shape = editor.getShape(shapeId)
        if (shape?.type === 'padlet-attachment-shape') {
          return shape
        }
        return null
      })
      .filter((shape): shape is PadletAttachmentShape => shape !== null)
    const allCids = attachmentShapes.map((shape) => `c${shape.props.postId}`)
    // Remove duplicates, since we can have multiple shapes linked to the same post
    return Array.from(new Set(allCids))
  })

  const currentFrameAttachments = computed<string[]>(() => {
    return currentFramePostCids.value
      .map((cid) => surfacePostsStore.savedPostEntitiesByCid[cid]?.attachment)
      .filter((attachment): attachment is string => attachment != null && attachment !== '')
  })

  /**
   * Returns true if all attachments have been loaded or errored on the current frame.
   */
  const hasLoadedOrErroredAllAttachments = computed(() => {
    const allLinkAttributes = currentFrameAttachments.value
      .map((url) => surfaceAttachmentsStore.getLinkAttributesForUrl(url))
      .filter((link): link is Link => link != null)
    return currentFrameAttachments.value.length === allLinkAttributes.length
  })

  const getLinkAttributesForShape = (shapeId: TLShapeId): Link | null => {
    const editor = useTldrawEditor().editor
    const shape = editor?.getShape(shapeId)
    if (shape == null) return null

    const post =
      shape.type === 'padlet-attachment-shape'
        ? surfacePostsStore.getPostByServerId((shape.props as PadletAttachmentShapeProps).postId.toString())
        : null
    if (post == null) return null

    const attachmentUrl = post.wish_content?.attachment_props?.url ?? post.attachment
    if (attachmentUrl == null) return null

    return surfaceAttachmentsStore.getLinkAttributesForUrl(attachmentUrl)
  }

  return {
    postCidsForLoadedAttachments,
    postCidsForErroredAttachments,
    addPostCidToLoadedAttachments,
    addPostCidToErroredAttachments,
    currentFrameAttachments,
    hasLoadedOrErroredAllAttachments,
    getLinkAttributesForShape,
  }
})
